import { useLazyQuery } from '@apollo/client';
import { QUERY_fetchOrderActivities } from 'queries';
import {
  FC,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createApolloCache, createApolloClient } from 'providers/ApolloProvider';
import { ActivitiesType } from 'types/users.types';

interface FetchOrderActivitiesResult {
  node: {
    activities?: {
      nodes?: ActivitiesType[];
      pageInfo?: { hasNextPage: boolean; endCursor: string | null };
    };
  } | null;
}

const PAGE_SIZE = 15;

const activitiesApolloClient = createApolloClient({ cache: createApolloCache() });

export interface ActivitiesDataContextType {
  activities: ActivitiesType[];
  isLoading: boolean;
  hasNextPage: boolean;
  loadMore: () => void;
}

export const ActivitiesDataContext = createContext<ActivitiesDataContextType>({
  activities: [],
  isLoading: true,
  hasNextPage: false,
  loadMore: () => {},
});

interface ActivitiesDataProviderProps {
  orderId: string;
}

export const ActivitiesDataProvider: FC<ActivitiesDataProviderProps> = ({ children, orderId }) => {
  const [activities, setActivities] = useState<ActivitiesType[]>([]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const endCursorRef = useRef<string | null>(null);

  const [fetchActivities, { loading }] = useLazyQuery<FetchOrderActivitiesResult>(
    QUERY_fetchOrderActivities,
    {
      client: activitiesApolloClient,
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        const connection = data?.node?.activities;
        if (!connection) return;
        const newNodes = connection.nodes ?? [];
        setActivities((prev) => (endCursorRef.current ? [...prev, ...newNodes] : newNodes));
        setHasNextPage(connection.pageInfo?.hasNextPage ?? false);
        endCursorRef.current = connection.pageInfo?.endCursor ?? null;
      },
    },
  );

  useEffect(() => {
    if (orderId) {
      endCursorRef.current = null;
      fetchActivities({ variables: { id: orderId, first: PAGE_SIZE } });
    }
  }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMore = useCallback(() => {
    if (hasNextPage && endCursorRef.current && !loading) {
      fetchActivities({
        variables: { id: orderId, first: PAGE_SIZE, after: endCursorRef.current },
      });
    }
  }, [hasNextPage, loading, orderId, fetchActivities]);

  const value = useMemo(
    () => ({
      activities,
      isLoading: loading && activities.length === 0,
      hasNextPage,
      loadMore,
    }),
    [activities, loading, hasNextPage, loadMore],
  );

  return <ActivitiesDataContext.Provider value={value}>{children}</ActivitiesDataContext.Provider>;
};

export const useActivitiesDataContext = () => useContext(ActivitiesDataContext);
